import { searchItems as search } from '@cerebro-diem/core';
import type { SearchHit as CoreSearchHit, SearchIo, SearchTarget, SearchType } from '@cerebro-diem/core';
import { containerPath } from './containers';
import { invokeEdgeFunction } from './edgeFunctions';
import { supabase } from './supabase';

/** One search result, with the page that opens it. */
export interface SearchHit extends CoreSearchHit {
  to: string;
}

export interface SearchResults {
  hits: SearchHit[];
  /** Types that returned SEARCH_LIMIT rows, so more of them match than are shown. */
  fullTypes: SearchType[];
  /** True when the meaning half could not run, so these are text matches only. The page says so. */
  meaningUnavailable: boolean;
}

/** The reads a search makes, from this site's Supabase client as the signed-in user. */
const io: SearchIo = {
  matchingRows: async (table, select, filter, limit) => {
    const { data, error } = await supabase.from(table).select(select).or(filter).limit(limit);
    if (error) throw error;
    return data ?? [];
  },
  rowsById: async (table, select, ids) => {
    const { data, error } = await supabase.from(table).select(select).in('id', ids);
    if (error) throw error;
    return data ?? [];
  },
  semanticSearch: (query) => invokeEdgeFunction('semantic-search', { query }),
  searchEmbeddings: async (args) => {
    const { data, error } = await supabase.rpc('search_embeddings', args);
    if (error) throw error;
    return data;
  },
};

/** The page a result opens. An unfiled capture opens its filing form on Today. */
function pathTo(target: SearchTarget): string {
  switch (target.kind) {
    case 'task':
      return `/tasks/${target.id}`;
    case 'note':
      return `/notes/${target.id}`;
    case 'person':
      return `/people/${target.id}`;
    case 'project':
    case 'area':
    case 'resource':
      return containerPath(target.kind, target.id);
    case 'capture':
      return `/?capture=${encodeURIComponent(target.id)}`;
  }
}

/**
 * Every task, note, project, area, resource, person and capture of the
 * signed-in user that matches `query`, for the chosen types, in one ranked
 * list (the search itself is core's `searchItems`). Throws when the text
 * search fails; when the meaning half fails, the reason is logged, because a
 * degraded search should be explicable from the console.
 */
export async function searchItems(query: string, types: readonly SearchType[]): Promise<SearchResults> {
  const results = await search(io, query, types);
  if (results.meaningFailure !== null) {
    console.warn(`search by meaning is unavailable: ${results.meaningFailure}`);
  }
  return {
    hits: results.hits.map((hit) => ({ ...hit, to: pathTo(hit.target) })),
    fullTypes: results.fullTypes,
    meaningUnavailable: results.meaningFailure !== null,
  };
}
